import { useState } from 'react'
import type { CreateOperatingCostEntryInput } from '@/lib/api/operating-costs-admin.service'
import type { OperatingCostCategory, OperatingCostEntry } from '@/types/api'
import {
  formatOperatingCostAmount,
  formatOperatingCostRate,
} from '../lib/operating-costs'
import { OperatingCostEntryForm } from './OperatingCostEntryForm'
import styles from '../CustosOperacionaisPage.module.css'

type OperatingCostEntryRowProps = {
  category: OperatingCostCategory
  entry: OperatingCostEntry
  isSaving: boolean
  onUpdate: (entryId: number, input: CreateOperatingCostEntryInput) => Promise<boolean>
  onDelete: (entryId: number) => Promise<boolean>
  onDuplicate?: (input: CreateOperatingCostEntryInput) => Promise<boolean>
}

export function OperatingCostEntryRow({
  category,
  entry,
  isSaving,
  onUpdate,
  onDelete,
  onDuplicate,
}: OperatingCostEntryRowProps) {
  const [mode, setMode] = useState<'edit' | 'duplicate' | null>(null)
  const [pendingDelete, setPendingDelete] = useState(false)
  const isPercent = category.value_type === 'percent'

  if (mode !== null)
    return (
      <OperatingCostEntryForm
        key={mode}
        category={category}
        entry={entry}
        mode={mode}
        isSaving={isSaving}
        onCancel={() => setMode(null)}
        onSubmit={async (input) => {
          const ok =
            mode === 'edit' ? await onUpdate(entry.id, input) : await onDuplicate!(input)
          if (ok) setMode(null)
          return ok
        }}
      />
    )

  return (
    <div className={styles.entryRow}>
      <div className={styles.entryMeta}>
        <span className={styles.entrySubtitle}>{entry.subtitle}</span>
        <span className={styles.entryValue}>
          {isPercent
            ? formatOperatingCostRate(entry.rate ?? 0)
            : formatOperatingCostAmount(entry.amount ?? 0)}
        </span>
        {!isPercent ? (
          <span className={styles.entrySubtitle}>
            {entry.competence_month
              ? `Competência: ${entry.competence_month.split('-').reverse().join('/')}`
              : 'Sem competência — fora do gráfico mensal'}
          </span>
        ) : null}
      </div>
      <div className={styles.entryActions}>
        <button
          type="button"
          className={styles.ghostButton}
          disabled={isSaving}
          onClick={() => {
            setMode('edit')
            setPendingDelete(false)
          }}
        >
          Editar
        </button>
        {onDuplicate ? (
          <button
            type="button"
            className={styles.ghostButton}
            disabled={isSaving}
            onClick={() => {
              setMode('duplicate')
              setPendingDelete(false)
            }}
          >
            Duplicar
          </button>
        ) : null}
        {pendingDelete ? (
          <>
            <button
              type="button"
              className={styles.dangerButton}
              disabled={isSaving}
              onClick={() => {
                void onDelete(entry.id)
              }}
            >
              {isSaving ? 'Excluindo...' : 'Confirmar'}
            </button>
            <button
              type="button"
              className={styles.ghostButton}
              disabled={isSaving}
              onClick={() => setPendingDelete(false)}
            >
              Cancelar
            </button>
          </>
        ) : (
          <button
            type="button"
            className={styles.ghostButton}
            disabled={isSaving}
            onClick={() => setPendingDelete(true)}
          >
            Excluir
          </button>
        )}
      </div>
    </div>
  )
}
